import {
  Divider,
  Typography
} from '@mui/material';
import Stack from '@mui/material/Stack';
import TextField from '@mui/material/TextField';
import React from 'react';
import {SupportTicketResolutionEnum} from '../../../../model/support/support-ticket-resolution.enum';
import {SupportTicketTypeEnum} from '../../../../model/support/support-ticket-type.enum';
import {SupportTicketModel} from '../../../../model/support/support-ticket.model';
import { SupportTicketUtils } from '../utils/support-ticket.utils';
import { Box, FormControl, InputLabel, MenuItem, Select } from '@mui/material';

type Props = {
  ticket: SupportTicketModel
  setTicket: (ticket: SupportTicketModel) => void
  translate: (key: string) => string
  translateLocal: (key: string) => string
  isDisabled: boolean
}

export const SupportTicketDrawerResolution = ({ticket, setTicket, isDisabled, translate, translateLocal}: Props) => {
  const buildHeader = () => (
    <Box mt={1} pt={1}>
      <Box display="flex" alignItems="center" ml={0.5} mb={0.5}>
        {SupportTicketUtils.getResolutionIcon(ticket.resolution) && (
          <Box
            mr={0.5}
            color={SupportTicketUtils.getResolutionIconColor(ticket.resolution)}
            display="flex"
            alignItems="center"
          >
            {React.createElement(SupportTicketUtils.getResolutionIcon(ticket.resolution), { fontSize: "small" })}
          </Box>
        )}
        <Typography variant="body1" fontWeight={600} letterSpacing={1} mr={1} lineHeight={1}>
          Rozwiązanie zgłoszenia:
        </Typography>
      </Box>
      <Divider sx={{ my: 1 }} />
    </Box>
  )

  const translateOption = (value: string) => translate(`support.ticket.resolution.${value}.option`)

  const buildClassifiedTypeSelect = () => (
    <FormControl fullWidth>
      <InputLabel shrink>{translateLocal('ticket.drawer.classifiedType')}</InputLabel>
      <Select
        displayEmpty
        label={translateLocal('ticket.drawer.classifiedType')}
        value={ticket.classifiedType}
        onChange={(e) => setTicket({ ...ticket, classifiedType: e.target.value as SupportTicketTypeEnum, classifiedErrorType: e.target.value === 'ERROR' ? ticket.classifiedErrorType : null })}
        sx={{
          py: 0.4,
          '& .MuiSelect-select': {
            py: 0.9,
            display: 'flex',
            alignItems: 'center',
          },
          '& .MuiSelect-displayEmpty': {
            color: (theme) => theme.palette.text.disabled,
            display: 'flex',
            alignItems: 'center',
          },
        }}
      >
        <MenuItem value="ERROR">{translate('support.ticket.type.ERROR')}</MenuItem>
        <MenuItem value="IMPROVEMENT">{translate('support.ticket.type.IMPROVEMENT')}</MenuItem>
      </Select>
    </FormControl>
  );

  const buildErrorTypeInput = () => (
    <FormControl fullWidth>
      <InputLabel shrink>{translateLocal('ticket.drawer.classifiedErrorType')}</InputLabel>
      <Select
        disabled={ticket.classifiedType === 'IMPROVEMENT'}
        displayEmpty
        label={translateLocal('column.classifiedErrorType')}
        value={ticket.classifiedErrorType || ''}
        onChange={(e) => setTicket({ ...ticket, classifiedErrorType: e.target.value })}
        sx={{
          py: 0.4,
          '& .MuiSelect-select': {
            py: 0.9,
            display: 'flex',
            alignItems: 'center',
          },
          '& .MuiSelect-displayEmpty': {
            color: (theme) => theme.palette.text.disabled,
            display: 'flex',
            alignItems: 'center',
          },
        }}
      >
        <MenuItem value="CRITICAL_ERROR">{translate('support.ticket.error_type.CRITICAL_ERROR')}</MenuItem>
        <MenuItem value="MAJOR_ERROR">{translate('support.ticket.error_type.MAJOR_ERROR')}</MenuItem>
        <MenuItem value="OTHER_ERROR">{translate('support.ticket.error_type.OTHER_ERROR')}</MenuItem>
        <MenuItem value="UPDATE_ERROR">{translate('support.ticket.error_type.UPDATE_ERROR')}</MenuItem>
      </Select>
    </FormControl>
  );



  const buildResolveOption = () => (
    <Box display="flex" alignItems="center" pl={0.5}>
      <Typography
        variant="body2"
        sx={{ whiteSpace: 'nowrap', pr: 2 }}
      >
        Sposób rozwiązania:
      </Typography>

      <FormControl fullWidth>
        <Select
          value={ticket.resolution || ''}
          displayEmpty
          disabled={isDisabled}
          aria-placeholder={translate('common.option.select')}
          onChange={(e) => {
            if(e.target.value !== SupportTicketResolutionEnum.FIX_SCHEDULED) {
              setTicket({...ticket, resolution: e.target.value, jiraTicket: null, estimatedFixDate: null})
            } else {
              setTicket({...ticket, resolution: e.target.value})
            }
          }}
          sx={{
            py: 0.40,
            '& .MuiSelect-select': {
              py: 0.40,
              display: 'flex',
              alignItems: 'center',
            },
          }}
        >
          <MenuItem value="FIX_SCHEDULED">
            {translateOption('FIX_SCHEDULED')}
          </MenuItem>
          <MenuItem value="REJECTED">
            {translateOption('REJECTED')}
          </MenuItem>
          <MenuItem value="CANNOT_REPRODUCE">
            {translateOption('CANNOT_REPRODUCE')}
          </MenuItem>
          <MenuItem value="LICENSE_UPGRADE_REQUIRED">
            {translateOption('LICENSE_UPGRADE_REQUIRED')}
          </MenuItem>
          <MenuItem value="FEATURE_NOT_EXISTS">
            {translateOption('FEATURE_NOT_EXISTS')}
          </MenuItem>
        </Select>
      </FormControl>
    </Box>
  );

  const buildMessageToUser = () =>  <Box mt={3}>
    <TextField
      label={translateLocal('ticket.drawer.message')}
      value={ticket.message}
      disabled={isDisabled}
      onChange={(e) => setTicket({...ticket, message: e.target.value})}
      required
      multiline
      minRows={5}
      maxRows={5}
      fullWidth
      sx={{
        '& .MuiInputBase-root': {
          overflow: 'auto',
        },
      }}
    />
  </Box>

  return <>
    {buildHeader()}
    {buildResolveOption()}
    {buildMessageToUser()}
  </>
}